/**
 * Utility functions for batching operations
 */

import { ProjectFilesResponse, SitelifeFile } from 'src/file/types';

/**
 * Splits an array into chunks of specified size
 * @param array - Array to split
 * @param chunkSize - Size of each chunk
 * @returns Array of chunks
 */
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    throw new Error('Chunk size must be greater than 0');
  }

  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Creates a consistent query key for batched project requests
 * @param projectIds - Array of project IDs in the batch
 * @returns Query key array
 */
export function createBatchQueryKey(
  projectIds: string[],
): (string | string[])[] {
  return ['projects', 'bulk', 'files', 'admin2', projectIds.sort()]; // Sort for consistent cache keys
}

/**
 * Transforms bulk API response into a lookup map for efficient access
 * @param bulkResponse - Response from bulk API
 * @returns Map of projectId to files array
 */
export function createProjectFilesMap(
  bulkResponse: ProjectFilesResponse[],
): Map<string, SitelifeFile[]> {
  const projectFilesMap = new Map<string, SitelifeFile[]>();

  if (Array.isArray(bulkResponse)) {
    bulkResponse.forEach((response) => {
      if (response?.projectId && Array.isArray(response?.files)) {
        projectFilesMap.set(response.projectId, response.files);
      }
    });
  }

  return projectFilesMap;
}
